import React from 'react';
import { useAuth } from '../../hooks/useAuth';
import { usePermission } from '@/hooks/usePermission.ts';
import { useEntitlement, MODULES } from '@/hooks/useEntitlement.ts';
import { useRollout, ROLLOUTS } from '@/hooks/useRollout.ts';
import { SettingsLayout, type SettingsNavSection } from '@/components/settings/SettingsLayout.tsx';

/**
 * Organization Admin: everything that configures the organization, a group per
 * topic. A feature's own area in the main nav (VPN, Notifications) keeps what is
 * yours and what you do day to day; its organization-wide pages are a group here.
 */
const AdminLayout: React.FC = () => {
  const { user } = useAuth();
  const perms = user?.permissions ?? [];
  const has = (p: string) => perms.includes(p);

  // The notification and VPN groups are gated as they were in their own areas.
  // Entitlement asks whether the plan bought routing, permission asks whether this
  // person may see it: neither substitutes for the other, and a nav item that led to
  // a section rendering nothing would be worse than no nav item.
  const routingEntitled = useEntitlement(MODULES.notificationRoutes);
  const canReadRoutes = usePermission('notification_route:read');
  const showRouting = routingEntitled && canReadRoutes;
  // A beta: PSA connections are route targets, so they sit with routing.
  const psaTicketing = useRollout(ROLLOUTS.psaTicketing);
  const canReadVpn = usePermission('vpn:read');

  const sections: SettingsNavSection[] = [
    {
      label: 'User management',
      visible: has('user:read'),
      items: [{ to: '/admin/users', label: 'Users' }],
    },
    {
      label: 'Permissions',
      visible: has('role:read') || has('team:read') || has('context:read'),
      items: [
        { to: '/admin/permissions', label: 'Overview' },
        ...(has('context:read') ? [{ to: '/admin/contexts', label: 'Contexts' }] : []),
        ...(has('team:read') ? [{ to: '/admin/teams', label: 'Teams' }] : []),
        ...(has('role:read') ? [{ to: '/admin/roles', label: 'Roles' }] : []),
      ],
    },
    {
      label: 'Organization',
      // The customer surfaces moved to the primary nav (F8), so `msp_customer:read`
      // no longer contributes an item here and is no longer part of what makes
      // this section visible.
      visible: has('org:read') || has('division:read') || has('region:read') || has('tag_definition:read'),
      items: [
        ...(has('org:read') ? [{ to: '/admin/general', label: 'General' }] : []),
        ...(has('division:read') ? [{ to: '/admin/divisions', label: 'Divisions' }] : []),
        ...(has('region:read') ? [{ to: '/admin/regions', label: 'Regions' }] : []),
        ...(has('tag_definition:read') ? [{ to: '/admin/tags', label: 'Tags' }] : []),
        ...(has('org:read') ? [{ to: '/admin/network-defaults', label: 'Network defaults' }] : []),
      ],
    },
    {
      label: 'Change management',
      visible: has('org:read'),
      // Not "Settings": the mobile strip drops the group labels, and the VPN's
      // settings page would be a second link of the same name.
      items: [{ to: '/admin/change-management', label: 'Approvals & auto-start', feature: 'changeManagement' }],
    },
    {
      label: 'Monitoring',
      visible: has('alert_definition:read'),
      // "Audit catalog" here is *device* audits — scheduled checks over device state —
      // and has nothing to do with the Activity Log below. The names are close enough
      // that they are deliberately kept in separate sections. Maintenance windows only
      // stop alerts paging, and are read under the same permission.
      items: [
        { to: '/admin/audit-catalog', label: 'Audit catalog' },
        { to: '/admin/maintenance-windows', label: 'Maintenance windows', feature: 'maintenanceWindows' },
      ],
    },
    {
      label: 'Notifications',
      visible: showRouting,
      items: [
        { to: '/admin/notifications/routing', label: 'Routing' },
        { to: '/admin/notifications/destinations', label: 'Shared destinations' },
        ...(psaTicketing ? [{ to: '/admin/notifications/psa', label: 'PSA ticketing' }] : []),
      ],
    },
    {
      label: 'VPN',
      visible: canReadVpn,
      items: [
        { to: '/admin/vpn/sites', label: 'Sites' },
        { to: '/admin/vpn/policies', label: 'Policies' },
        { to: '/admin/vpn/settings', label: 'Settings' },
      ],
    },
    {
      label: 'Security',
      // Every assistant's grant is filtered per row by `context:manage_teams` over
      // its context, so that is who has a list to see.
      visible: has('activity_log:read') || has('context:manage_teams'),
      items: [
        ...(has('activity_log:read') ? [{ to: '/admin/activity-log', label: 'Activity log', feature: 'activityLog' as const }] : []),
        ...(has('context:manage_teams') ? [{ to: '/admin/ai-assistants', label: 'AI assistants' }] : []),
      ],
    },
    {
      label: 'Billing',
      visible: has('billing:read'),
      items: [
        { to: '/admin/billing', label: 'Usage & invoices', feature: 'billing' },
        { to: '/admin/plans', label: 'Plans & pricing', feature: 'billing' },
      ],
    },
  ];

  return <SettingsLayout sections={sections} ariaLabel="Admin sections" />;
};

export default AdminLayout;
